
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Review } from "@/types/review";

export const useReviewData = (id: string | undefined) => {
  const [review, setReview] = useState<Review | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCurrentUserReview, setIsCurrentUserReview] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userVoteType, setUserVoteType] = useState<"up" | "down" | null>(null);

  useEffect(() => {
    const fetchReview = async () => {
      if (!id || id === 'new') {
        setIsLoading(false);
        return;
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        // Fetch review with exact vote counts directly from the database
        const { data: reviewData, error: reviewError } = await supabase
          .from('reviews')
          .select(`
            *,
            profiles(username, verification_status, is_creative)
          `)
          .eq('id', id)
          .maybeSingle();
        
        if (reviewError) throw reviewError;
        
        if (!reviewData) {
          setIsLoading(false);
          return;
        }

        const { data: airportData } = await supabase
          .from('airports')
          .select('id')
          .eq('name', reviewData.airport_name)
          .maybeSingle();

        const { data: reportData, error: reportError } = await supabase
          .from('review_reports')
          .select('review_id')
          .eq('review_id', id)
          .maybeSingle();

        if (reportError) throw reportError;
        
        // Get user's vote on this review if logged in
        if (user) {
          const { data: voteData } = await supabase
            .from('review_votes')
            .select('vote_type')
            .eq('review_id', id)
            .eq('user_id', user.id)
            .maybeSingle();
            
          if (voteData) {
            // Convert database vote type to component vote type
            setUserVoteType(voteData.vote_type === 'upvote' ? 'up' : 'down');
          }
        }

        // Get exact count of upvotes and downvotes
        const { count: upvoteCount, error: upvoteError } = await supabase
          .from('review_votes')
          .select('*', { count: 'exact', head: true })
          .eq('review_id', id)
          .eq('vote_type', 'upvote');
          
        if (upvoteError) throw upvoteError;
        
        const { count: downvoteCount, error: downvoteError } = await supabase
          .from('review_votes')
          .select('*', { count: 'exact', head: true })
          .eq('review_id', id)
          .eq('vote_type', 'downvote');
          
        if (downvoteError) throw downvoteError;

        const reviewWithStringCoords: Review = {
          ...reviewData,
          reported: !!reportData,
          airport_id: airportData?.id,
          latitude: reviewData.latitude?.toString(),
          longitude: reviewData.longitude?.toString(),
          terminal: reviewData.terminal || undefined,
          upvotes: upvoteCount || 0,
          downvotes: downvoteCount || 0
        };

        setReview(reviewWithStringCoords);
        
        if (user && reviewData) {
          setIsCurrentUserReview(user.id === reviewData.user_id);
        }

        if (user) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('is_admin')
            .eq('id', user.id)
            .single();
          
          setIsAdmin(!!profileData?.is_admin);
        }
      } catch (error) {
        console.error('Error fetching review:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReview();
  }, [id]);

  return {
    review,
    setReview,
    isLoading,
    isCurrentUserReview,
    isAdmin,
    userVoteType
  };
};
